import {
    Camera,
    Loader2,
    Maximize2,
    Minimize2,
    Pause,
    PictureInPicture2,
    Play,
    Volume2,
    VolumeX,
} from 'lucide-react';
import React from 'react';
import bocian_bg from '../assets/images/bocian-bg.jpg';
import brandLogo from '../assets/images/Logo.png';
import { StreamPrivacyMask } from '../components/StreamPrivacyMask';
import { StreamPrivacyMaskCanvas } from '../components/StreamPrivacyMaskCanvas';
import { VideoJS } from '../components/videojs';
import { STREAM_HLS_URL } from '../config/app';
import { STREAM_PRIVACY_MASK } from '../config/streamPrivacy';
import { canBakePrivacyInBrowser, needsCanvasPrivacyMask } from '../utils/privacyMaskPlatform';
import { bakePrivacyStream } from '../utils/bakePrivacyStream';
import {
    captureVideoFrameWithLogo,
    downloadPngBlob,
    screenshotFilename,
} from '../utils/captureVideoFrameWithLogo';

const SCREENSHOT_COOLDOWN_MS = 1000;

const CONTROL_BTN_CLASS =
    'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-black/50 text-white transition-colors duration-200 hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-40';

function ControlBtn({ children, className = '', ...rest }) {
    return (
        <button type="button" className={`${CONTROL_BTN_CLASS} ${className}`} {...rest}>
            {children}
        </button>
    );
}

function getFullscreenElement() {
    const d = document;
    return d.fullscreenElement ?? d.webkitFullscreenElement ?? d.mozFullScreenElement ?? null;
}

function LiveBadge() {
    return (
        <div className="absolute right-6 top-6 z-30 flex items-center gap-2 rounded-xl bg-red-600/90 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white shadow-lg backdrop-blur-sm animate-pulse">
            Live
        </div>
    );
}

export default function Player() {
    const stageRef = React.useRef(null);
    const playerRef = React.useRef(null);
    const [player, setPlayer] = React.useState(null);
    const [playing, setPlaying] = React.useState(false);
    const [buffering, setBuffering] = React.useState(false);
    const [fullscreen, setFullscreen] = React.useState(false);
    const [pipActive, setPipActive] = React.useState(false);
    const [pipSupported, setPipSupported] = React.useState(false);
    const [muted, setMuted] = React.useState(false);
    const [screenshotBusy, setScreenshotBusy] = React.useState(false);
    const [screenshotToastKind, setScreenshotToastKind] = React.useState(null);
    const [videoEl, setVideoEl] = React.useState(null);
    const [pseudoFs, setPseudoFs] = React.useState(false);
    const canvasPrivacyMask = needsCanvasPrivacyMask();
    const canBake = React.useMemo(
        () => STREAM_PRIVACY_MASK.enabled && canBakePrivacyInBrowser(),
        []
    );
    // Wariant A aktywny: maska wypalana w piksele (działa w PiP/fullscreen).
    const bakingActive = canBake && playing && Boolean(videoEl);
    const screenshotToastTimerRef = React.useRef(null);
    const lastScreenshotOkAtRef = React.useRef(0);
    const bakedVideoRef = React.useRef(null);

    const videoJsOptions = React.useMemo(
        () => ({
            autoplay: 'any',
            controls: false,
            responsive: true,
            fluid: false,
            language: 'pl',
            muted: false,
            playsinline: true,
            preload: 'auto',
            bigPlayButton: false,
            controlBar: false,
            loadingSpinner: false,
            titleBar: false,
            html5: {
                vhs: {
                    overrideNative: true,
                    smoothPlaylistUpdate: true,
                    enableLowInitialPlaylist: true,
                },
            },
            sources: [{ src: STREAM_HLS_URL, type: 'application/x-mpegURL' }],
            poster: bocian_bg,
        }),
        []
    );

    const handlePlayerReady = React.useCallback((p) => {
        playerRef.current = p;
        setPlayer(p);
    }, []);

    React.useEffect(() => {
        if (!player || player.isDisposed()) return undefined;

        const onPlay = () => setPlaying(true);
        const onPause = () => {
            setPlaying(false);
            setBuffering(false);
        };
        const onWaiting = () => setBuffering(true);
        const onPlayingEv = () => setBuffering(false);
        const onCanPlay = () => setBuffering(false);
        const onVolumeChange = () => {
            if (!player.isDisposed()) {
                setMuted(player.muted() || player.volume() === 0);
            }
        };

        player.on('play', onPlay);
        player.on('pause', onPause);
        player.on('waiting', onWaiting);
        player.on('playing', onPlayingEv);
        player.on('canplay', onCanPlay);
        player.on('volumechange', onVolumeChange);

        setPlaying(!player.paused());
        onVolumeChange();

        const setupVideo = () => {
            const v = player.el()?.querySelector('video');
            if (!v) return;
            setVideoEl(v);
        };

        player.on('loadedmetadata', setupVideo);
        if (player.readyState() >= 1) {
            setupVideo();
        }

        const onVisibility = () => {
            if (document.visibilityState !== 'visible' || player.isDisposed()) return;
            if (!player.paused()) return;
            player.play()?.catch(() => {});
        };
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            if (!player.isDisposed()) {
                player.off('play', onPlay);
                player.off('pause', onPause);
                player.off('waiting', onWaiting);
                player.off('playing', onPlayingEv);
                player.off('canplay', onCanPlay);
                player.off('volumechange', onVolumeChange);
                player.off('loadedmetadata', setupVideo);
            }
        };
    }, [player]);

    // Wariant A: wypalanie maski w piksele + odtwarzanie wypalonego strumienia.
    React.useEffect(() => {
        if (!bakingActive || !videoEl) return undefined;
        const bv = bakedVideoRef.current;
        if (!bv) return undefined;

        const ctrl = bakePrivacyStream(videoEl, { fps: 30 });
        if (!ctrl) return undefined; // brak wsparcia → zostaje wariant B (nakładka)

        bv.srcObject = ctrl.stream;
        bv.muted = true; // audio leci ze źródłowego <video>; tu tylko obraz
        bv.play?.().catch(() => {});

        const onPipEnter = () => setPipActive(true);
        const onPipLeave = () => setPipActive(false);
        bv.addEventListener('enterpictureinpicture', onPipEnter);
        bv.addEventListener('leavepictureinpicture', onPipLeave);

        return () => {
            bv.removeEventListener('enterpictureinpicture', onPipEnter);
            bv.removeEventListener('leavepictureinpicture', onPipLeave);
            ctrl.stop();
            bv.srcObject = null;
        };
    }, [bakingActive, videoEl]);

    // PiP dostępny tylko gdy potrafimy wypalić maskę (inaczej PiP zgubiłby maskę).
    React.useEffect(() => {
        setPipSupported(
            canBake &&
                typeof document !== 'undefined' &&
                document.pictureInPictureEnabled === true
        );
    }, [canBake]);

    // Pseudo-fullscreen (CSS): wyjście klawiszem Escape.
    React.useEffect(() => {
        if (!pseudoFs) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setPseudoFs(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [pseudoFs]);

    React.useEffect(() => {
        return () => {
            if (screenshotToastTimerRef.current) {
                clearTimeout(screenshotToastTimerRef.current);
            }
        };
    }, []);

    const showScreenshotFeedback = React.useCallback((kind) => {
        if (screenshotToastTimerRef.current) {
            clearTimeout(screenshotToastTimerRef.current);
        }
        setScreenshotToastKind(kind);
        const ms = kind === 'cooldown' ? 2400 : 2600;
        screenshotToastTimerRef.current = setTimeout(() => {
            setScreenshotToastKind(null);
            screenshotToastTimerRef.current = null;
        }, ms);
    }, []);

    React.useEffect(() => {
        const sync = () => {
            const stage = stageRef.current;
            const docFs = getFullscreenElement();
            const v = playerRef.current?.el()?.querySelector('video');
            setFullscreen(
                Boolean(
                    (stage && docFs === stage) || (v && docFs === v)
                )
            );
        };
        document.addEventListener('fullscreenchange', sync);
        document.addEventListener('webkitfullscreenchange', sync);
        document.addEventListener('mozfullscreenchange', sync);
        sync();
        return () => {
            document.removeEventListener('fullscreenchange', sync);
            document.removeEventListener('webkitfullscreenchange', sync);
            document.removeEventListener('mozfullscreenchange', sync);
        };
    }, []);

    const getPlayer = () => {
        const p = playerRef.current;
        return p && !p.isDisposed() ? p : null;
    };

    const togglePlay = () => {
        const p = getPlayer();
        if (!p) return;
        if (p.paused()) p.play()?.catch(() => {});
        else p.pause();
    };

    const toggleMute = () => {
        const p = getPlayer();
        if (!p) return;
        if (p.muted() || p.volume() === 0) {
            p.muted(false);
            if (p.volume() === 0) p.volume(1);
        } else {
            p.muted(true);
        }
    };

    const toggleFullscreen = async () => {
        const stage = stageRef.current;
        if (!stage) return;
        const doc = document;
        const fsEl = getFullscreenElement();

        // Wyjście: natywny fullscreen kontenera albo pseudo-fullscreen CSS.
        if (fsEl) {
            try {
                if (doc.exitFullscreen) await doc.exitFullscreen();
                else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen();
                else if (doc.mozCancelFullScreen) doc.mozCancelFullScreen();
            } catch {}
            return;
        }
        if (pseudoFs) {
            setPseudoFs(false);
            return;
        }

        // Fullscreen KONTENERA (nie samego <video>) — nakładka/maska zostaje.
        try {
            if (stage.requestFullscreen) {
                await stage.requestFullscreen();
                return;
            }
            if (stage.webkitRequestFullscreen) {
                stage.webkitRequestFullscreen();
                return;
            }
            if (stage.mozRequestFullScreen) {
                stage.mozRequestFullScreen();
                return;
            }
        } catch {}

        // Brak natywnego fullscreenu elementu (np. iPhone) → pseudo-fullscreen CSS.
        setPseudoFs(true);
    };

    const togglePip = async () => {
        const v = bakedVideoRef.current; // wypalone wideo (z maską)
        if (!v) return;
        try {
            if (document.pictureInPictureElement === v) await document.exitPictureInPicture();
            else await v.requestPictureInPicture();
        } catch {}
    };

    const startPlayback = () => {
        getPlayer()?.play()?.catch(() => {});
    };

    const takeScreenshot = async () => {
        const v =
            bakingActive && bakedVideoRef.current
                ? bakedVideoRef.current
                : getPlayer()?.el()?.querySelector('video');
        if (!v || screenshotBusy) return;
        const now = Date.now();
        if (
            lastScreenshotOkAtRef.current > 0 &&
            now - lastScreenshotOkAtRef.current < SCREENSHOT_COOLDOWN_MS
        ) {
            showScreenshotFeedback('cooldown');
            return;
        }
        setScreenshotBusy(true);
        try {
            const blob = await captureVideoFrameWithLogo(v, brandLogo, {
                maskAlreadyApplied: bakingActive,
            });
            downloadPngBlob(blob, screenshotFilename());
            lastScreenshotOkAtRef.current = Date.now();
            showScreenshotFeedback('success');
        } catch (e) {
            const msg =
                e?.name === 'SecurityError' || e?.message?.includes('insecure')
                    ? 'Przeglądarka zablokowała zapis obrazu (ograniczenia streamu sieciowego).'
                    : e?.message ||
                      'Nie udało się zapisać zrzutu. Upewnij się, że obraz ze streamu jest już widoczny.';
            window.alert(msg);
        } finally {
            setScreenshotBusy(false);
        }
    };

    const isFullscreen = fullscreen || pseudoFs;
    const stageClass =
        'player-stage relative aspect-video overflow-hidden bg-black fullscreen:aspect-auto fullscreen:h-full fullscreen:min-h-0 fullscreen:w-full' +
        (pseudoFs ? ' player-stage--pseudofs' : '');

    return (
        <div className="rounded-3xl border border-gray-200 bg-white p-2">
            <div className="overflow-hidden rounded-2xl bg-black ring-8 ring-white">
                <div ref={stageRef} className={stageClass}>
                    <VideoJS
                        options={videoJsOptions}
                        onReady={handlePlayerReady}
                        className={`absolute inset-0 h-full w-full${bakingActive ? ' opacity-0' : ''}`}
                    />

                    {/* Wariant A: wypalone wideo z maską w pikselach (PiP/fullscreen-safe). */}
                    {bakingActive && (
                        <video
                            ref={bakedVideoRef}
                            className="absolute inset-0 z-[11] h-full w-full bg-black object-contain"
                            muted
                            playsInline
                            aria-hidden
                        />
                    )}

                    {/* Wariant B: nakładka DOM — tylko gdy nie wypalamy w pikselach. */}
                    {STREAM_PRIVACY_MASK.enabled && !bakingActive && <StreamPrivacyMask />}
                    {STREAM_PRIVACY_MASK.enabled && !bakingActive && (
                        <StreamPrivacyMaskCanvas
                            videoEl={videoEl}
                            active={canvasPrivacyMask && playing}
                        />
                    )}

                    {buffering && playing && (
                        <div
                            className="pointer-events-none absolute inset-0 z-[15] flex items-center justify-center bg-black/15"
                            aria-hidden
                        >
                            <Loader2 className="h-11 w-11 animate-spin text-white" strokeWidth={2} />
                        </div>
                    )}

                    {!playing && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/10">
                            <button
                                type="button"
                                onClick={startPlayback}
                                title="Uruchom transmisję na żywo"
                                className="flex h-20 w-20 items-center justify-center rounded-full bg-main-red text-white transition hover:scale-[1.04] hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-black/30 motion-reduce:hover:scale-100"
                                aria-label="Odtwórz"
                            >
                                <Play className="ml-1 h-10 w-10" fill="currentColor" strokeWidth={0} />
                            </button>
                        </div>
                    )}

                    {playing && (
                        <div className="pointer-events-auto absolute bottom-0 left-0 right-0 z-20 flex min-h-[7.5rem] sm:min-h-[8.5rem] flex-col justify-end pb-0 group">
                            <div
                                className="w-full bg-gradient-to-t from-black/70 via-black/25 to-transparent px-3 pb-3 pt-10 opacity-0 transition-opacity duration-[520ms] ease-out group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100 motion-reduce:opacity-100 motion-reduce:transition-none"
                            >
                                <div className="pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto [@media(hover:none)]:pointer-events-auto relative mx-auto flex w-full max-w-full items-center justify-between gap-2 sm:gap-3">
                                <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                                    <ControlBtn
                                        onClick={togglePlay}
                                        title="Wstrzymaj odtwarzanie"
                                        aria-label="Pauza"
                                    >
                                        <Pause className="h-5 w-5" strokeWidth={2} />
                                    </ControlBtn>
                                    <ControlBtn
                                        onClick={toggleMute}
                                        title={muted ? 'Włącz dźwięk' : 'Wycisz dźwięk'}
                                        aria-label={muted ? 'Włącz dźwięk' : 'Wycisz'}
                                    >
                                        {muted ? (
                                            <VolumeX className="h-5 w-5" strokeWidth={2} />
                                        ) : (
                                            <Volume2 className="h-5 w-5" strokeWidth={2} />
                                        )}
                                    </ControlBtn>
                                </div>
                                <div className="pointer-events-auto absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                                    <ControlBtn
                                        onClick={takeScreenshot}
                                        disabled={screenshotBusy}
                                        title={
                                            screenshotBusy
                                                ? 'Trwa zapisywanie…'
                                                : 'Zapisz kadr obrazu'
                                        }
                                        aria-label="Zrzut ekranu"
                                        aria-busy={screenshotBusy}
                                    >
                                        {screenshotBusy ? (
                                            <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
                                        ) : (
                                            <Camera className="h-5 w-5" strokeWidth={2} />
                                        )}
                                    </ControlBtn>
                                </div>
                                <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                                    {pipSupported && (
                                        <ControlBtn
                                            onClick={togglePip}
                                            title={
                                                pipActive
                                                    ? 'Wyłącz tryb obrazu w obrazie'
                                                    : 'Obraz w oknie (obraz w obrazie)'
                                            }
                                            aria-label={
                                                pipActive
                                                    ? 'Wyłącz obraz w obrazie'
                                                    : 'Obraz w obrazie'
                                            }
                                            className={pipActive ? 'bg-main-green/55 hover:bg-main-green/65' : ''}
                                        >
                                            <PictureInPicture2 className="h-5 w-5" strokeWidth={2} />
                                        </ControlBtn>
                                    )}
                                    <ControlBtn
                                        onClick={toggleFullscreen}
                                        title={
                                            isFullscreen
                                                ? 'Wróć z pełnego ekranu'
                                                : 'Pełny ekran odtwarzacza'
                                        }
                                        aria-label={
                                            isFullscreen ? 'Zamknij pełny ekran' : 'Pełny ekran'
                                        }
                                    >
                                        {isFullscreen ? (
                                            <Minimize2 className="h-5 w-5" strokeWidth={2} />
                                        ) : (
                                            <Maximize2 className="h-5 w-5" strokeWidth={2} />
                                        )}
                                    </ControlBtn>
                                </div>
                            </div>
                        </div>
                        </div>
                    )}

                    {screenshotToastKind && (
                        <div
                            role="status"
                            aria-live="polite"
                            className="pointer-events-none absolute left-1/2 top-6 z-[36] max-w-[min(92vw,22rem)] -translate-x-1/2 rounded-3xl border border-gray-200 bg-white px-5 py-2.5 shadow-sm"
                        >
                            <p className="text-center text-sm font-medium text-gray-800">
                                {screenshotToastKind === 'success'
                                    ? 'Zapisano kadr z życia bocianów'
                                    : 'Poczekaj chwilę między zrzutami.'}
                            </p>
                        </div>
                    )}

                    <LiveBadge />
                </div>
            </div>
        </div>
    );
}
